import { MutationResult } from '@apollo/client';
import { isEmpty } from 'lodash';
import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import {
  StartAiDispositiveProvisionWorkflowForTeMutation,
  useStartAiDispositiveProvisionWorkflowForTeMutation,
} from '@/modules/aiDP/graphql/TriggerAIDPSuggestions.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

interface UseStartAIDPWorkflowForTEResult {
  startWorkflow: (
    testamentaryEntityIDs: string[],
    documentIDs: string[]
  ) => Promise<void>;
  mutationResults: MutationResult<StartAiDispositiveProvisionWorkflowForTeMutation>;
}

/**
 * Hook to handle starting the AI DP workflow for extracting dispositions
 * for testamentary entities, from the given documents.
 */
export function useStartAIDPWorkflowForTE(): UseStartAIDPWorkflowForTEResult {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const trackUserEvent = useTrackUserEvent();

  const { householdId } = useHouseholdDetailsContext();

  const [startWorkflowMutation, mutationResults] =
    useStartAiDispositiveProvisionWorkflowForTeMutation({
      onError: (error) => {
        reportError(
          'error in useStartAiDispositiveProvisionWorkflowForTeMutation',
          error
        );
      },
      onCompleted: () => {
        showFeedback('Successfully started data extraction', {
          variant: 'success',
          autoHideDuration: 3000,
        });
        trackUserEvent('ai_dp start workflow clicked for TE');
      },
    });

  const startWorkflow = useCallback(
    async (testamentaryEntityIDs: string[], documentIDs: string[]) => {
      if (
        !householdId ||
        isEmpty(testamentaryEntityIDs) ||
        isEmpty(documentIDs)
      ) {
        return;
      }

      await startWorkflowMutation({
        variables: {
          input: {
            householdID: householdId,
            documentIDs,
            testamentaryEntityIDs,
          },
        },
      });
    },
    [householdId, startWorkflowMutation]
  );

  return {
    startWorkflow,
    mutationResults,
  };
}
